import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Upload, Trash2, Save, RefreshCw, ImagePlus } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import {
  getGallery, uploadGalleryImage, updateGalleryImage, deleteGalleryImage, addGalleryImage,
  resolveAssetUrl,
} from '../services/api';

const MAX_UPLOAD_MB = 10;

const GalleryTab = () => {
  const { toast } = useToast();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [urlForm, setUrlForm] = useState({ url: '', alt: '' });
  const fileInputRef = useRef(null);

  const fetchImages = async () => {
    setLoading(true);
    try {
      const data = await getGallery();
      setImages(data || []);
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo cargar la galería', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchImages(); }, []);

  const handleUpload = async (file, altText = '') => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Error', description: 'Solo archivos de imagen (jpg, png, webp, gif)', variant: 'destructive' });
      return;
    }
    if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
      toast({ title: 'Error', description: `Archivo demasiado grande (máx ${MAX_UPLOAD_MB} MB)`, variant: 'destructive' });
      return;
    }
    setUploading(true);
    try {
      const created = await uploadGalleryImage(file, altText || file.name);
      setImages((p) => [...p, created]);
      toast({ title: '✅ Foto subida', description: file.name });
    } catch (e) {
      toast({ title: 'Error al subir', description: e.message, variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const handleFilesSelected = async (fileList) => {
    const files = Array.from(fileList || []);
    for (const f of files) {
      // Sequential upload so we don't overwhelm the backend
      // eslint-disable-next-line no-await-in-loop
      await handleUpload(f);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handFilesSafe(e.dataTransfer.files);
    }
  };

  const handFilesSafe = (fl) => handleFilesSelected(fl);

  const handleAddByUrl = async () => {
    if (!urlForm.url.trim()) {
      toast({ title: 'Error', description: 'URL requerida', variant: 'destructive' });
      return;
    }
    try {
      const created = await addGalleryImage({ url: urlForm.url.trim(), alt: urlForm.alt.trim() });
      setImages((p) => [...p, created]);
      setUrlForm({ url: '', alt: '' });
      toast({ title: '✅ Imagen agregada' });
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const handleSaveAlt = async (img) => {
    try {
      const updated = await updateGalleryImage(img.id, { alt: img.alt, url: img.url });
      setImages((p) => p.map((i) => (i.id === updated.id ? updated : i)));
      toast({ title: '✅ Imagen actualizada' });
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const handleDelete = async (img) => {
    if (!window.confirm('¿Eliminar esta imagen de la galería?')) return;
    try {
      await deleteGalleryImage(img.id);
      setImages((p) => p.filter((i) => i.id !== img.id));
      toast({ title: '🗑️ Imagen eliminada' });
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const updateImageField = (id, field, value) => {
    setImages((p) => p.map((i) => (i.id === id ? { ...i, [field]: value } : i)));
  };

  if (loading) return <div className="flex items-center justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="gallery-tab">
      {/* Uploader */}
      <Card>
        <CardHeader>
          <CardTitle>Galería</CardTitle>
          <CardDescription>{images.length} imágenes. Arrastra archivos o usa una URL externa.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              dragActive ? 'border-pink-500 bg-pink-50' : 'border-gray-300 hover:border-pink-400 hover:bg-pink-50/50'
            }`}
            data-testid="gallery-dropzone"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              multiple
              className="hidden"
              onChange={(e) => handleFilesSelected(e.target.files)}
              data-testid="gallery-file-input"
            />
            <Upload className="w-10 h-10 mx-auto text-gray-400 mb-2" />
            <p className="text-sm text-gray-600">
              {uploading ? 'Subiendo…' : dragActive ? 'Suelta para subir' : 'Arrastra imágenes o haz clic para seleccionar'}
            </p>
            <p className="text-xs text-gray-400 mt-1">JPG, PNG, WebP, GIF · máx {MAX_UPLOAD_MB} MB</p>
          </div>

          {/* Add by URL */}
          <div className="border-t pt-4 space-y-2">
            <Label className="text-xs font-semibold">O agregar desde una URL externa</Label>
            <div className="grid md:grid-cols-3 gap-2">
              <Input
                className="md:col-span-2"
                value={urlForm.url}
                onChange={(e) => setUrlForm({ ...urlForm, url: e.target.value })}
                placeholder="https://…"
                data-testid="gallery-url-input"
              />
              <Input
                value={urlForm.alt}
                onChange={(e) => setUrlForm({ ...urlForm, alt: e.target.value })}
                placeholder="Texto alternativo"
                data-testid="gallery-alt-input"
              />
            </div>
            <Button onClick={handleAddByUrl} variant="outline" data-testid="gallery-add-url-btn">
              <ImagePlus className="w-4 h-4 mr-1" />Agregar por URL
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Grid of existing images */}
      <div className="grid md:grid-cols-3 gap-4">
        {images.map((img) => (
          <Card key={img.id} data-testid={`gallery-item-${img.id}`}>
            <div className="aspect-video bg-gray-100 overflow-hidden rounded-t-lg">
              <img
                src={resolveAssetUrl(img.url)}
                alt={img.alt}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.style.opacity = '0.3'; }}
              />
            </div>
            <CardContent className="pt-3 space-y-2">
              <Input
                value={img.alt || ''}
                onChange={(e) => updateImageField(img.id, 'alt', e.target.value)}
                placeholder="Texto alternativo"
                data-testid={`gallery-alt-${img.id}`}
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={() => handleSaveAlt(img)} className="bg-black hover:bg-gray-800 flex-1" data-testid={`gallery-save-${img.id}`}>
                  <Save className="w-4 h-4 mr-1" />Guardar
                </Button>
                <Button size="sm" variant="destructive" onClick={() => handleDelete(img)} data-testid={`gallery-delete-${img.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-[10px] text-gray-400 break-all">{img.url}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default GalleryTab;
